import { useState, useCallback } from "react"
import { initiateIntegrationAuthApi } from "@/api/integrations"

/**
 * Get OAuth provider for integration ID
 */
function getProviderForIntegration(integrationId: string): string {
  if (integrationId.includes("gmail") || integrationId.includes("google")) {
    return "google"
  }
  if (integrationId.includes("outlook")) {
    return "microsoft"
  }
  return "google"
}

/**
 * Hook to handle OAuth flow for connecting integrations
 */
export function useConnectIntegration() {
  const [connectingId, setConnectingId] = useState<string | null>(null)

  const connect = useCallback(async (integrationId: string): Promise<boolean> => {
    try {
      setConnectingId(integrationId)
      const provider = getProviderForIntegration(integrationId)
      const result = await initiateIntegrationAuthApi({ provider })

      // Open OAuth popup
      const width = 500
      const height = 600
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2

      const popup = window.open(
        result.authUrl,
        "oauth-nylas",
        `width=${width},height=${height},left=${left},top=${top}`
      )

      // Return promise that resolves when OAuth completes
      return new Promise<boolean>((resolve) => {
        const handleMessage = (event: MessageEvent) => {
          // Validate origin to prevent malicious postMessage attacks
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
          const expectedOrigin = new URL(apiBaseUrl).origin;
          if (event.origin !== expectedOrigin) {
            return; // Ignore messages from unexpected origins
          }

          if (event.data.type === "oauth-success" && event.data.provider === "nylas") {
            cleanup()
            popup?.close()
            resolve(true)
          } else if (event.data.type === "oauth-error" && event.data.provider === "nylas") {
            cleanup()
            popup?.close()
            console.error("OAuth error:", event.data.error)
            resolve(false)
          }
        }

        const cleanup = () => {
          window.removeEventListener("message", handleMessage)
          clearInterval(checkPopup)
          setConnectingId(null)
        }

        window.addEventListener("message", handleMessage)

        // Check if popup is closed manually
        const checkPopup = setInterval(() => {
          if (popup?.closed) {
            cleanup()
            resolve(false)
          }
        }, 500)
      })
    } catch (error) {
      console.error("Failed to initiate connection:", error)
      setConnectingId(null)
      return false
    }
  }, [])

  return {
    connect,
    connectingId,
    isConnecting: connectingId !== null,
  }
}